import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface FieldErrors {
  name?: string;
  email?: string;
  phone?: string;
  password?: string;
  confirmPassword?: string;
  acceptTerms?: string;
}

type Step = "form" | "verify";

export function RegisterPage() {
  const navigate = useNavigate();
  const { registerPending, resendVerificationCode, verifyEmail, isAuthenticated } = useAuth();

  const [step, setStep] = useState<Step>("form");

  // Paso 1 - formulario
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Paso 2 - verificación
  const [pendingEmail, setPendingEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const otpInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Cooldown del botón reenviar
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (step === "verify") {
      setTimeout(() => otpInputRef.current?.focus(), 100);
    }
  }, [step]);

  useEffect(() => {
  const stateEmail = (location.state as any)?.email;
  if (stateEmail) {
    setEmail(stateEmail);
    setStep("form"); 
  }
}, [location.state]);

  // ============================================================
  // Validaciones del paso 1
  // ============================================================
  const validateField = (field: keyof FieldErrors, value: string | boolean): string | undefined => {
    switch (field) {
      case "name": {
        const v = String(value).trim();
        if (!v) return "El nombre es requerido";
        if (v.length < 3) return "Mínimo 3 caracteres";
        if (v.length > 100) return "Máximo 100 caracteres";
        return undefined;
      }
      case "email": {
        const v = String(value).trim();
        if (!v) return "El correo es requerido";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "Correo electrónico inválido";
        return undefined;
      }
      case "phone": {
        const v = String(value).trim();
        if (!v) return undefined;
        const digits = v.replace(/\D/g, "");
        if (digits.length < 10) return "Mínimo 10 dígitos";
        if (digits.length > 15) return "Máximo 15 dígitos";
        return undefined;
      }
      case "password": {
        const v = String(value);
        if (!v) return "La contraseña es requerida";
        if (v.length < 8) return "Mínimo 8 caracteres";
        if (!/[A-Z]/.test(v)) return "Debe contener al menos una mayúscula";
        if (!/[a-z]/.test(v)) return "Debe contener al menos una minúscula";
        if (!/[0-9]/.test(v)) return "Debe contener al menos un número";
        if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(v))
          return "Debe contener al menos un carácter especial";
        return undefined;
      }
      case "confirmPassword": {
        const v = String(value);
        if (!v) return "Confirma tu contraseña";
        if (v !== password) return "Las contraseñas no coinciden";
        return undefined;
      }
      case "acceptTerms":
        return value ? undefined : "Debes aceptar los términos y condiciones";
      default:
        return undefined;
    }
  };

  const handleBlur = (field: keyof FieldErrors, value: string | boolean) => {
    setFieldErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const validateAll = (): boolean => {
    const errors: FieldErrors = {
      name: validateField("name", name),
      email: validateField("email", email),
      phone: validateField("phone", phone),
      password: validateField("password", password),
      confirmPassword: validateField("confirmPassword", confirmPassword),
      acceptTerms: validateField("acceptTerms", acceptTerms),
    };
    setFieldErrors(errors);
    return Object.values(errors).every((e) => !e);
  };

  // ============================================================
  // Submit paso 1: registrar pendiente y pasar a verificación
  // ============================================================
  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!validateAll()) {
      setError("Por favor corrige los errores del formulario");
      return;
    }

    setIsLoading(true);
    try {
      const result = await registerPending({
        email: email.trim(),
        name: name.trim(),
        password,
        phone: phone.trim() || undefined,
      });

      setPendingEmail(result.email);
      setOtp("");
      setOtpError("");
      setResendMessage("");
      setResendCooldown(60);
      setStep("verify");
    } catch (err: any) {
      const msg = err?.message || "Error al registrar. Intenta nuevamente.";
      setError(msg);
      if (msg.toLowerCase().includes("email")) {
        setFieldErrors((prev) => ({ ...prev, email: msg }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================
  // Submit paso 2: verificar OTP
  // ============================================================
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError("");

    if (!/^\d{6}$/.test(otp)) {
      setOtpError("El código debe tener 6 dígitos");
      return;
    }

    setIsVerifying(true);
    try {
      await verifyEmail(pendingEmail, otp);
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      setOtpError(err?.message || "Código inválido");
    } finally {
      setIsVerifying(false);
    }
  };

  // ============================================================
  // Reenviar código
  // ============================================================
  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setIsResending(true);
    setResendMessage("");
    setOtpError("");
    try {
      const res = await resendVerificationCode(pendingEmail);
      setResendMessage(res.message || "Código reenviado");
      setResendCooldown(60);
      setOtp("");
      otpInputRef.current?.focus();
    } catch (err: any) {
      setOtpError(err?.message || "Error al reenviar");
    } finally {
      setIsResending(false);
    }
  };

  // ============================================================
  // Indicador de fuerza de contraseña
  // ============================================================
  const getPasswordStrength = () => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) score++;
    return score;
  };

  const strength = getPasswordStrength();
  const strengthLabel = ["Muy débil", "Débil", "Aceptable", "Buena", "Fuerte", "Excelente"][strength];
  const strengthColor = [
    "bg-red-500",
    "bg-red-400",
    "bg-yellow-400",
    "bg-yellow-500",
    "bg-green-500",
    "bg-green-600",
  ][strength];

  // ============================================================
  // Render: PASO 2 — Verificación
  // ============================================================
  if (step === "verify") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-2xl p-10">
            <div className="text-center mb-6">
              <img src="/img/logo.png" alt="Gran Arquitectura" className="h-14 mx-auto mb-4" />
              <div className="text-4xl mb-2">📧</div>
              <h1 className="text-2xl font-bold text-slate-900">Verifica tu correo</h1>
              <p className="text-sm text-slate-500 mt-2">
                Enviamos un código de 6 dígitos a
              </p>
              <p className="text-sm font-semibold text-primary mt-1">{pendingEmail}</p>
            </div>

            <form onSubmit={handleVerify} className="space-y-5">
              <div>
                <label htmlFor="otp" className="block text-sm font-semibold text-slate-700 mb-1.5 text-center">
                  Código de verificación
                </label>
                <input
                  ref={otpInputRef}
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  disabled={isVerifying}
                  className="w-full text-center text-3xl tracking-[0.5em] py-4 border-2 border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none font-mono"
                />
              </div>

              {otpError && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3 text-center">
                  {otpError}
                </div>
              )}
              {resendMessage && (
                <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg p-3 text-center">
                  {resendMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={isVerifying || otp.length !== 6}
                className="w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-lg font-semibold disabled:opacity-50 transition flex items-center justify-center gap-2"
              >
                {isVerifying ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Verificando...
                  </>
                ) : (
                  "Verificar y activar cuenta"
                )}
              </button>

              <div className="text-center">
                <p className="text-sm text-slate-500 mb-2">¿No recibiste el código?</p>
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={isResending || resendCooldown > 0}
                  className="text-sm text-primary hover:underline font-semibold disabled:opacity-50 disabled:no-underline"
                >
                  {isResending
                    ? "Reenviando..."
                    : resendCooldown > 0
                    ? `Reenviar en ${resendCooldown}s`
                    : "Reenviar código"}
                </button>
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-slate-200 text-center">
              <button
                onClick={() => {
                  setStep("form");
                  setOtp("");
                  setOtpError("");
                  setResendMessage("");
                }}
                className="text-xs text-slate-400 hover:text-primary"
              >
                ← Cambiar correo electrónico
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // Render: PASO 1 — Formulario
  // ============================================================
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="bg-white rounded-2xl shadow-2xl p-10">
          <div className="text-center mb-8">
            <img src="/img/logo.png" alt="Gran Arquitectura" className="h-14 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-900">Crear Cuenta</h1>
            <p className="text-sm text-slate-500 mt-1">
              Regístrate para acceder a tu portal de cliente
            </p>
          </div>

          <form onSubmit={handleRegister} className="space-y-5" noValidate>
            {/* Nombre */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-1.5">
                Nombre completo <span className="text-primary">*</span>
              </label>
              <input
                id="name"
                type="text"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => handleBlur("name", name)}
                placeholder="Juan Pérez"
                disabled={isLoading}
                className={`w-full px-4 py-3 border-2 rounded-lg bg-slate-50 focus:bg-white outline-none transition ${
                  fieldErrors.name
                    ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                    : "border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/15"
                }`}
              />
              {fieldErrors.name && <p className="text-xs text-red-600 mt-1">{fieldErrors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1.5">
                Correo electrónico <span className="text-primary">*</span>
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => handleBlur("email", email)}
                placeholder="tu@correo.com"
                disabled={isLoading}
                className={`w-full px-4 py-3 border-2 rounded-lg bg-slate-50 focus:bg-white outline-none transition ${
                  fieldErrors.email
                    ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                    : "border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/15"
                }`}
              />
              {fieldErrors.email && <p className="text-xs text-red-600 mt-1">{fieldErrors.email}</p>}
            </div>

            {/* Teléfono */}
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-1.5">
                Teléfono <span className="text-slate-400 font-normal">(opcional)</span>
              </label>
              <input
                id="phone"
                type="tel"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                onBlur={() => handleBlur("phone", phone)}
                placeholder="81 1234 5678"
                disabled={isLoading}
                className={`w-full px-4 py-3 border-2 rounded-lg bg-slate-50 focus:bg-white outline-none transition ${
                  fieldErrors.phone
                    ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                    : "border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/15"
                }`}
              />
              {fieldErrors.phone && <p className="text-xs text-red-600 mt-1">{fieldErrors.phone}</p>}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-slate-700 mb-1.5">
                Contraseña <span className="text-primary">*</span>
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onBlur={() => handleBlur("password", password)}
                  placeholder="••••••••"
                  disabled={isLoading}
                  className={`w-full px-4 py-3 pr-20 border-2 rounded-lg bg-slate-50 focus:bg-white outline-none transition ${
                    fieldErrors.password
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/15"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-sm"
                  tabIndex={-1}
                >
                  {showPassword ? "Ocultar" : "Ver"}
                </button>
              </div>
              {password && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 bg-slate-200 rounded-full h-1.5">
                    <div
                      className={`h-1.5 rounded-full transition-all ${strengthColor}`}
                      style={{ width: `${(strength / 5) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-500 min-w-[70px]">{strengthLabel}</span>
                </div>
              )}
              {fieldErrors.password && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.password}</p>
              )}
              <p className="text-xs text-slate-400 mt-1">
                Mínimo 8 caracteres, mayúscula, minúscula, número y carácter especial
              </p>
            </div>

            {/* Confirm Password */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-semibold text-slate-700 mb-1.5"
              >
                Confirmar contraseña <span className="text-primary">*</span>
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => handleBlur("confirmPassword", confirmPassword)}
                  placeholder="••••••••"
                  disabled={isLoading}
                  className={`w-full px-4 py-3 pr-20 border-2 rounded-lg bg-slate-50 focus:bg-white outline-none transition ${
                    fieldErrors.confirmPassword
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-slate-200 focus:border-primary focus:ring-4 focus:ring-primary/15"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 text-sm"
                  tabIndex={-1}
                >
                  {showConfirm ? "Ocultar" : "Ver"}
                </button>
              </div>
              {fieldErrors.confirmPassword && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.confirmPassword}</p>
              )}
            </div>

            {/* Terms */}
            <div>
              <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => {
                    setAcceptTerms(e.target.checked);
                    if (e.target.checked) {
                      setFieldErrors((prev) => ({ ...prev, acceptTerms: undefined }));
                    }
                  }}
                  disabled={isLoading}
                  className="w-4 h-4 mt-0.5 accent-primary"
                />
                <span>
                  Acepto los{" "}
                  <a href="/terms" className="text-primary hover:underline font-medium">
                    términos y condiciones
                  </a>{" "}
                  y el{" "}
                  <a href="/privacy" className="text-primary hover:underline font-medium">
                    aviso de privacidad
                  </a>
                </span>
              </label>
              {fieldErrors.acceptTerms && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.acceptTerms}</p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-lg font-semibold disabled:opacity-50 transition flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Enviando código...
                </>
              ) : (
                "Crear Cuenta"
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500">
              ¿Ya tienes cuenta?{" "}
              <Link to="/login" className="text-primary hover:underline font-semibold">
                Inicia sesión aquí
              </Link>
            </p>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-100 text-center">
            <Link to="/" className="text-xs text-slate-400 hover:text-primary">
              ← Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}